import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { Fluid } from 'primeng/fluid';
import { SiteTemplateService, ValidateVersionResult } from '@/app/services/site-template.service';
import { BrandKit, BrandKitService } from '@/app/services/brand-kit.service';
import { TemplateInfo } from '@/app/components/template-info';
import { CategoryType, SiteFileDisposition, SiteTemplate } from '@/lib-backend';
import { environment } from '@/environments/environment';

/** Root S3 key prefix for site-template builds; the per-template base prefix is `${root}/${id}`. */
const SITE_TEMPLATES_PREFIX_ROOT = 'site-templates';

/** Real (selectable) category types — Unknown is excluded from the UI. */
const SELECTABLE_TYPES: { label: string; value: CategoryType }[] = [
    { label: 'Skill-based games', value: CategoryType.SkillBasedGames },
    { label: 'Lucky-based games', value: CategoryType.LuckyBasedGames },
];

@Component({
    selector: 'app-site-templates',
    standalone: true,
    imports: [
        FormsModule, Button, Dialog, InputText, Message, Select, TableModule, Tag, Toast, Tooltip, Fluid,
        TemplateInfo,
    ],
    providers: [MessageService],
    templateUrl: './site-templates.html',
})
export class SiteTemplates implements OnInit {
    protected readonly service = inject(SiteTemplateService);
    private readonly brandKitService = inject(BrandKitService);
    private readonly messageService = inject(MessageService);

    protected readonly typeOptions = SELECTABLE_TYPES;

    // ── Create / rename dialog ───────────────────────────────────────────────
    protected readonly dialogVisible = signal(false);
    /** null → add mode; otherwise rename mode. */
    protected readonly editing = signal<SiteTemplate | null>(null);
    protected readonly saving = signal(false);

    protected readonly mode = computed<'add' | 'edit'>(() => (this.editing() ? 'edit' : 'add'));
    protected readonly dialogTitle = computed(() =>
        this.mode() === 'add' ? 'Add Site Template' : `Rename — ${this.editing()!.Name}`
    );

    protected readonly id = signal('');
    protected readonly name = signal('');
    protected readonly type = signal<CategoryType>(CategoryType.SkillBasedGames);
    protected readonly bucket = signal('');     // stored value, shown read-only in edit mode
    protected readonly basePrefix = signal(''); // stored value, shown read-only in edit mode

    /** Fixed per-environment bucket used when adding a template. */
    protected readonly defaultBucket = environment.siteTemplatesBucket;
    /** Base prefix derived from the ID (`${root}/${id}`); shown read-only. */
    protected readonly derivedBasePrefix = computed(() => {
        const v = this.id().trim();
        return v ? `${SITE_TEMPLATES_PREFIX_ROOT}/${v}` : '';
    });

    protected readonly idError = signal<string | null>(null);
    protected readonly nameError = signal<string | null>(null);
    protected readonly generalError = signal<string | null>(null);

    // ── Source / publish dialog ──────────────────────────────────────────────
    protected readonly sourceDialogVisible = signal(false);
    protected readonly sourceTarget = signal<SiteTemplate | null>(null);
    protected readonly versions = signal<string[]>([]);
    protected readonly versionsLoading = signal(false);
    protected readonly publishing = signal(false);

    protected readonly selectedVersion = signal('');

    protected readonly validating = signal(false);
    protected readonly validateResult = signal<ValidateVersionResult | null>(null);
    protected readonly sourceError = signal<string | null>(null);

    // ── Template Info dialog ─────────────────────────────────────────────────
    protected readonly infoDialogVisible = signal(false);
    protected readonly infoTarget = signal<SiteTemplate | null>(null);
    protected readonly infoBrandKit = signal<BrandKit | null>(null);
    protected readonly infoLoading = signal(false);
    protected readonly infoEmpty = computed(() =>
        this.infoTarget()?.CurrentVersion
            ? 'This template ships no meta/brand-kit.json for this version.'
            : 'This template has no published version yet.'
    );

    protected readonly sourceTitle = computed(() => {
        const t = this.sourceTarget();
        return t ? `Source & publish — ${t.Name}` : 'Source & publish';
    });

    /** Version options for the dropdown, the live one flagged for the label. */
    protected readonly versionOptions = computed(() => {
        const current = this.sourceTarget()?.CurrentVersion;
        return this.versions().map(v => ({
            value: v,
            label: v === current ? `v${v} (current)` : `v${v}`,
        }));
    });

    ngOnInit(): void {
        this.service.loadTemplates();
    }

    protected refresh(): void {
        this.service.loadTemplates();
    }

    protected typeLabelOf(t: CategoryType): string {
        return this.typeOptions.find(o => o.value === t)?.label ?? t;
    }

    protected trackById(_index: number, item: SiteTemplate): string {
        return item.SiteTemplateId;
    }

    // ── Template Info ────────────────────────────────────────────────────────

    protected async openInfo(template: SiteTemplate): Promise<void> {
        this.infoTarget.set(template);
        this.infoBrandKit.set(null);
        this.infoLoading.set(template.CurrentVersion !== null);
        this.infoDialogVisible.set(true);
        if (!template.CurrentVersion) return;

        const kit = await this.brandKitService.resolve(template.SiteTemplateId);
        if (this.infoTarget()?.SiteTemplateId === template.SiteTemplateId) {
            this.infoBrandKit.set(kit);
            this.infoLoading.set(false);
        }
    }

    protected closeInfo(): void {
        this.infoDialogVisible.set(false);
    }

    // ── Create / rename ──────────────────────────────────────────────────────

    private clearErrors(): void {
        this.idError.set(null);
        this.nameError.set(null);
        this.generalError.set(null);
    }

    protected openCreate(): void {
        this.editing.set(null);
        this.id.set('');
        this.name.set('');
        this.type.set(CategoryType.SkillBasedGames);
        this.bucket.set('');
        this.basePrefix.set('');
        this.clearErrors();
        this.dialogVisible.set(true);
    }

    protected openRename(template: SiteTemplate): void {
        this.editing.set(template);
        this.id.set(template.SiteTemplateId);
        this.name.set(template.Name);
        this.type.set(template.CategoryType);
        this.bucket.set(template.Bucket);
        this.basePrefix.set(template.BasePrefix);
        this.clearErrors();
        this.dialogVisible.set(true);
    }

    protected close(): void {
        this.dialogVisible.set(false);
    }

    protected async save(): Promise<void> {
        if (this.mode() === 'add') {
            await this.create();
        } else {
            await this.rename();
        }
    }

    private async create(): Promise<void> {
        this.clearErrors();

        const id = this.id().trim();
        const name = this.name().trim();
        const bucket = this.defaultBucket;
        const basePrefix = this.derivedBasePrefix();
        let invalid = false;
        if (!id) { this.idError.set('ID is required'); invalid = true; }
        if (!name) { this.nameError.set('Name is required'); invalid = true; }
        if (!bucket) { this.generalError.set('Site templates bucket is not configured for this environment'); invalid = true; }
        if (invalid) return;

        this.saving.set(true);
        try {
            const { result } = await this.service.addTemplate({
                Id: id,
                CategoryType: this.type(),
                Name: name,
                Bucket: bucket,
                BasePrefix: basePrefix,
            });
            switch (result) {
                case 'Ok':
                    this.messageService.add({ severity: 'success', summary: 'Template created', detail: name });
                    this.close();
                    break;
                case 'AlreadyExists':
                    this.idError.set('A template with this ID already exists');
                    break;
                default:
                    this.generalError.set('Failed to create template');
            }
        } finally {
            this.saving.set(false);
        }
    }

    private async rename(): Promise<void> {
        this.clearErrors();

        const target = this.editing();
        if (!target) return;

        const name = this.name().trim();
        if (!name) { this.nameError.set('Name is required'); return; }

        this.saving.set(true);
        try {
            const { result } = await this.service.updateTemplate({
                SiteTemplateId: target.SiteTemplateId,
                Name: name,
            });
            switch (result) {
                case 'Ok':
                    this.messageService.add({ severity: 'success', summary: 'Template renamed', detail: name });
                    this.close();
                    break;
                case 'NotFound':
                    this.generalError.set('Template no longer exists');
                    break;
                default:
                    this.generalError.set('Failed to rename template');
            }
        } finally {
            this.saving.set(false);
        }
    }

    // ── Source / publish ─────────────────────────────────────────────────────

    protected async openSource(template: SiteTemplate): Promise<void> {
        this.sourceTarget.set(template);
        this.selectedVersion.set(template.CurrentVersion ?? '');
        this.validateResult.set(null);
        this.sourceError.set(null);
        this.versions.set([]);
        this.sourceDialogVisible.set(true);
        await this.reloadVersions();
    }

    protected closeSource(): void {
        this.sourceDialogVisible.set(false);
    }

    protected async reloadVersions(): Promise<void> {
        const target = this.sourceTarget();
        if (!target) return;
        this.versionsLoading.set(true);
        try {
            const { result, versions } = await this.service.listVersions(target.SiteTemplateId);
            if (result === 'Ok') {
                this.versions.set(versions);
            } else if (result === 'NotFound') {
                this.sourceError.set('Template no longer exists');
            } else {
                this.sourceError.set('Failed to list versions');
            }
        } finally {
            this.versionsLoading.set(false);
        }
    }

    protected async validate(): Promise<void> {
        const target = this.sourceTarget();
        if (!target) return;
        const version = this.selectedVersion().trim();
        if (!version) { this.sourceError.set('Select a version to validate'); return; }

        this.sourceError.set(null);
        this.validateResult.set(null);
        this.validating.set(true);
        try {
            const res = await this.service.validateVersion({
                SiteTemplateId: target.SiteTemplateId,
                BucketOverride: null,
                BasePrefixOverride: null,
                Version: version,
            });
            this.validateResult.set(res);
        } finally {
            this.validating.set(false);
        }
    }

    protected async publish(): Promise<void> {
        const target = this.sourceTarget();
        if (!target) return;
        const version = this.selectedVersion().trim();
        if (!version) { this.sourceError.set('Select a version to publish'); return; }

        this.sourceError.set(null);
        this.publishing.set(true);
        try {
            const { result, template } = await this.service.setSource({
                SiteTemplateId: target.SiteTemplateId,
                Bucket: null,
                BasePrefix: null,
                Version: version,
            });
            switch (result) {
                case 'Ok':
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Published',
                        detail: `${target.Name} now serves v${version}`,
                    });
                    if (template) {
                        this.sourceTarget.set(template);
                    }
                    this.closeSource();
                    break;
                case 'NotFound':
                    this.sourceError.set('Template no longer exists');
                    break;
                case 'VersionNotInStorage':
                    this.sourceError.set('This version is missing or empty in storage — CI may not have finished.');
                    break;
                case 'Conflict':
                    this.sourceError.set('A concurrent edit changed this template. Reloading versions — retry.');
                    await this.reloadVersions();
                    break;
                default:
                    this.sourceError.set('Failed to publish version');
            }
        } finally {
            this.publishing.set(false);
        }
    }

    // ── Disposition presentation ─────────────────────────────────────────────

    protected dispositionSeverity(d: SiteFileDisposition): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (d) {
            case SiteFileDisposition.Immutable: return 'success';
            case SiteFileDisposition.Html: return 'info';
            case SiteFileDisposition.Revalidate: return 'warn';
            case SiteFileDisposition.Deny: return 'danger';
            default: return 'secondary';
        }
    }

    protected dispositionLabel(d: SiteFileDisposition): string {
        return d === SiteFileDisposition.Unknown ? 'unknown' : d;
    }
}
